import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Chip, Dialog, DialogContent, DialogTitle, IconButton, Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

import Langs, { Localize } from "../../app/lang/langs";
import styles from "./SuggestedProfiles.module.css";
import NetworkManager from "../../app/managers/networkManager";
import { clearSuggestionProfile, getVReferenceProfile } from "../vreference/vreferenceSlice";

/**
 * One referred candidate, opened from the suggestions panel in the Opportunity form.
 *
 * Read-only by construction: there is no save path here, and the backend's `profile`
 * endpoint is the only thing this calls. An `opportunities:edit` user can see who the person
 * is and open their CV, but cannot edit or delete the contact — that stays with the
 * colleague who referred them and with V-Reference managers.
 *
 * The HR-only fields (referral status, HR review) are absent unless the viewer ALSO holds
 * `vreference:edit`; the server decides that and simply does not send them otherwise, so
 * `contact.status` being undefined is the normal case here rather than an error.
 *
 * Rendered above a PrimeReact Dialog, hence the explicit zIndex — PrimeReact's overlay sits
 * above MUI's default 1300 and would otherwise cover this.
 */
export default function SuggestedProfileModal({ idVReference, onHide }) {

    const dispatch = useDispatch()

    const contact = useSelector((state) => state.vreference.suggestionProfile)
    const attachments = useSelector((state) => state.vreference.suggestionAttachments)
    const lang = useSelector((state) => state.settings.lang)

    useEffect(() => {
        if (idVReference) {
            dispatch(getVReferenceProfile({ id: idVReference }))
        } else {
            dispatch(clearSuggestionProfile())
        }
    }, [idVReference, dispatch])

    const row = (Icon, value, href) => {
        if (!value) return null
        return (
            <div className={styles.detailRow}>
                <Icon className={styles.detailIcon} fontSize="small" />
                {href
                    ? <a className={styles.detailLink} href={href}>{value}</a>
                    : <span>{value}</span>}
            </div>
        )
    }

    return (
        <Dialog
            open={!!idVReference}
            onClose={onHide}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: 2000 }}
            PaperProps={{ className: styles.profilePaper }}
        >
            <DialogTitle className={styles.profileTitle}>
                <span>
                    {contact
                        ? `${contact.first_name} ${contact.last_name}`
                        : <Langs str="VREF_SUGGEST_PROFILE" />}
                </span>
                <IconButton size="small" onClick={onHide}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {!contact ? (
                    <div className={styles.hint}><Langs str="VREF_SUGGEST_LOADING" /></div>
                ) : (
                    <>
                        <div className={styles.detailBlock}>
                            {row(WorkOutlineIcon, contact.poste_name || contact.poste_label)}
                            {row(BusinessOutlinedIcon, contact.company)}
                            {row(MailOutlineIcon, contact.email, contact.email ? `mailto:${contact.email}` : null)}
                            {row(PhoneOutlinedIcon, contact.phone, contact.phone ? `tel:${contact.phone}` : null)}
                        </div>

                        <div className={styles.referredLine}>
                            <Langs str="VREFERENCE_REFERRED_BY" />: <strong>{contact.created_by_name || '-'}</strong>
                        </div>

                        {contact.notes ? (
                            <>
                                <span className={styles.sectionLabel}><Langs str="VREFERENCE_NOTES" /></span>
                                <div className={styles.notes}>{contact.notes}</div>
                            </>
                        ) : null}

                        {/* Present only for a viewer who also holds vreference:edit. */}
                        {contact.status ? (
                            <div className={styles.statusLine}>
                                <span className={styles.sectionLabel}><Langs str="VREFERENCE_STATUS" /></span>
                                <Chip
                                    size="small"
                                    className={styles.cvChip}
                                    label={Localize(`VREFERENCE_STATUS_${contact.status.toUpperCase()}`, lang)}
                                />
                            </div>
                        ) : null}

                        <span className={styles.sectionLabel}><Langs str="VREFERENCE_CV_SECTION" /></span>
                        {attachments.length ? (
                            <div className={styles.cvList}>
                                {attachments.map(attachment => (
                                    <div key={attachment.id} className={styles.cvRow}>
                                        <span className={styles.cvName}>{attachment.original_name}</span>
                                        <Tooltip title={Localize("VREFERENCE_OPEN_CV", lang)}>
                                            <IconButton
                                                size="small"
                                                onClick={() => window.open(
                                                    NetworkManager.getInstance()
                                                        .getVReferenceAttachmentUrl(attachment.id),
                                                    '_blank', 'noopener')}
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.hint}><Langs str="VREFERENCE_NO_CV" /></div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
